import React, { useState } from "react";
import { Form, Badge, Card, Row, Col, Container } from "react-bootstrap";
import { CiSearch } from "react-icons/ci";
import { 
  FaFileAlt, 
  FaDownload, 
  FaEye, 
  FaCalendarAlt, 
  FaChartBar,
  FaChartLine,
  FaTable,
  FaUsers,
  FaBoxes,
  FaSyringe,
  FaTruck,
  FaExclamationTriangle,
  FaLock,
  FaUnlock,
  FaClipboardList,
} from "react-icons/fa";
import SplitPane from "react-split-pane";
import UseToggle from "../../../Hooks/UseToggle";
import ReportViewer from "./ReportsComponent/ReportViewer";
import ReportExport from "./ReportsComponent/ReportExport";

const Reports = () => {
  const [searchData, setSearchData] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const toggles = UseToggle();

  // Report definitions
  const reports = [
    {
      id: "enrollment",
      name: "Enrollment Report",
      description: "Screened, randomized and status by site",
      icon: <FaUsers />,
      category: "Subject",
      tags: ["Enrollment", "Sites", "Status"],
      lastRun: "20 Jun 2026, 14:30",
      runs: 23,
    },
    {
      id: "randomization",
      name: "Randomization Report",
      description: "Randomization numbers, dates and arms",
      icon: <FaChartBar />,
      category: "Subject",
      tags: ["Randomization", "Arms", "Dates"],
      lastRun: "19 Jun 2026, 10:15",
      runs: 18,
    },
    {
      id: "schema-r",
      name: "Schema Assignment Report",
      description: "Schemas, scope and subjects randomized",
      icon: <FaClipboardList />,
      category: "Subject",
      tags: ["Schema", "Assignment", "Scope"],
      lastRun: "18 Jun 2026, 16:45",
      runs: 12,
    },
    {
      id: "depot-inv",
      name: "Depot Inventory Report",
      description: "Available, in-prep and quarantine by depot",
      icon: <FaBoxes />,
      category: "Inventory",
      tags: ["Depot", "Stock", "Quarantine"],
      lastRun: "20 Jun 2026, 09:00",
      runs: 31,
    },
    {
      id: "site-inv-r",
      name: "Site Inventory Report",
      description: "Stock on hand and resupply status",
      icon: <FaBoxes />,
      category: "Inventory",
      tags: ["Site", "Stock", "Resupply"],
      lastRun: "19 Jun 2026, 11:30",
      runs: 27,
    },
    {
      id: "kit-acct",
      name: "Kit Accountability Report",
      description: "Full kit lifecycle and disposition",
      icon: <FaSyringe />,
      category: "Inventory",
      tags: ["Kit", "Lifecycle", "Disposition"],
      lastRun: "20 Jun 2026, 08:45",
      runs: 35,
    },
    {
      id: "expiry",
      name: "Expiry Report",
      description: "Kits expiring within 90 days and expired",
      icon: <FaExclamationTriangle />,
      category: "Inventory",
      tags: ["Expiry", "Kits", "Alert"],
      lastRun: "20 Jun 2026, 07:00",
      runs: 42,
    },
    {
      id: "shipment",
      name: "Shipment Report",
      description: "All shipments with status and tracking",
      icon: <FaTruck />,
      category: "Supply Chain",
      tags: ["Shipments", "Tracking", "Status"],
      lastRun: "18 Jun 2026, 13:20",
      runs: 16,
    },
    {
      id: "unblind-r",
      name: "Emergency Unblinding Report",
      description: "Code break events and justifications",
      icon: <FaUnlock />,
      category: "Safety",
      tags: ["Unblinding", "Emergency", "Events"],
      lastRun: "19 Jun 2026, 15:00",
      runs: 8,
    },
    {
      id: "ureq-r",
      name: "Unblinding Request Report",
      description: "Subject-level requests, status and approvals",
      icon: <FaClipboardList />,
      category: "Safety",
      tags: ["Requests", "Status", "Approvals"],
      lastRun: "17 Jun 2026, 12:00",
      runs: 11,
    },
    {
      id: "blindmatrix-r",
      name: "Blinding Access Matrix",
      description: "Each user's side and unblinding permissions",
      icon: <FaLock />,
      category: "Compliance",
      tags: ["Blinding", "Access", "Users"],
      lastRun: "16 Jun 2026, 09:30",
      runs: 6,
    },
    {
      id: "screening-r",
      name: "Screening Report",
      description: "Screening activity and failure reasons",
      icon: <FaUsers />,
      category: "Subject",
      tags: ["Screening", "Failures", "Reasons"],
      lastRun: "20 Jun 2026, 10:00",
      runs: 14,
    },
  ];

  // Report categories
  const categories = ["All", "Subject", "Inventory", "Supply Chain", "Safety", "Compliance"];

  // Filter reports
  const filteredReports = reports.filter((report) => {
    const search = searchData.toLowerCase();
    const matchSearch = 
      report.name?.toLowerCase().includes(search) ||
      report.description?.toLowerCase().includes(search) ||
      report.tags.some(tag => tag.toLowerCase().includes(search)) ||
      report.id?.toLowerCase().includes(search);
    
    const matchCategory = !categoryFilter || categoryFilter === "All" || report.category === categoryFilter;
    
    return matchSearch && matchCategory;
  });

  // Stats
  const totalReports = reports.length;
  const totalRuns = reports.reduce((sum, r) => sum + r.runs, 0);

  // Handler functions
  const handleRunReport = (report) => {
    setSelectedReport(report);
    setLoading(true);
    // Simulate report generation
    setTimeout(() => {
      setLoading(false);
      setReportData({
        report: report,
        generated: new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
        rows: 12,
        data: generateMockData(report.id),
      });
      toggles.setToggle1();
    }, 1500);
  };

  const handleExportReport = (report) => {
    setSelectedReport(report);
    toggles.setToggle2();
  };

  // Generate mock data based on report type
  const generateMockData = (reportId) => {
    const mockData = {
      enrollment: [
        { site: "101", name: "Memorial Cancer Center", screened: 12, randomized: 8, screenFailed: 2 },
        { site: "102", name: "Riverside Oncology Institute", screened: 9, randomized: 6, screenFailed: 1 },
        { site: "201", name: "Universitätsklinikum Frankfurt", screened: 8, randomized: 5, screenFailed: 2 },
        { site: "202", name: "Charité Berlin", screened: 5, randomized: 3, screenFailed: 1 },
      ],
      randomization: [
        { subject: "101-001", randNo: "R0001", site: "101", schema: "RS-001", date: "15 Mar 2026", arm: "Arm A" },
        { subject: "101-002", randNo: "R0002", site: "101", schema: "RS-001", date: "16 Mar 2026", arm: "Arm B" },
        { subject: "102-001", randNo: "R0003", site: "102", schema: "RS-001", date: "16 Mar 2026", arm: "Arm C" },
      ],
      expiry: [
        { kit: "K00007", batch: "B24-A019", expiry: "15 Feb 2027", daysLeft: -30, location: "Site 101" },
        { kit: "K00001", batch: "B24-A019", expiry: "20 Jul 2027", daysLeft: 385, location: "Depot-NA" },
        { kit: "K00002", batch: "B24-B044", expiry: "15 Aug 2027", daysLeft: 411, location: "Depot-NA" },
      ],
      default: [
        { col1: "Data 1", col2: "Data 2", col3: "Data 3" },
        { col1: "Data 4", col2: "Data 5", col3: "Data 6" },
      ],
    };
    return mockData[reportId] || mockData.default;
  };

  // Get category color
  const getCategoryColor = (category) => {
    switch(category) {
      case "Subject": return "primary";
      case "Inventory": return "success";
      case "Supply Chain": return "warning";
      case "Safety": return "danger";
      case "Compliance": return "info";
      default: return "secondary";
    }
  };

  return (
    <>
      {/* Toolbar */}
      <div
        className="justify-content-between toolbar border-bottom hstack p-1"
        style={{ height: 36 }}
      >
        <div className="hstack gap-2">
          <div className="position-relative">
            <Form.Control
              type="Search"
              onChange={(e) => setSearchData(e.target.value)}
              value={searchData}
              placeholder="Search..."
              style={{ width: 200, padding: "3px 10px" }}
            />
            <CiSearch
              className="CP position-absolute bg-white"
              title="Search"
              style={{ width: 15, top: 4, right: 7 }}
            />
          </div>
          <Form.Select
            className="w-auto"
            style={{ width: 200, padding: "3px 10px" }}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            {categories.map((cat, i) => (
              <option key={i} value={cat}>{cat}</option>
            ))}
          </Form.Select>
        </div>
        <div className="text-muted small">
          {filteredReports.length} report(s) · {totalRuns} total runs
        </div>
      </div>

      {/* Stats Cards */}
      <div className="px-2 py-1 border-bottom">
        <Row className="g-2">
          <Col xs={6} md={3} className="mb-2 pt-2">
            <Card className="app-card bg-light border h-100">
              <Card.Body>
                <div className="d-flex gap-3">
                  <div className="study_icopn">
                    <FaFileAlt className="bg-white text-primary shadow-sm" />
                  </div>
                  <div>
                    <Card.Title style={{ fontSize: 16 }}>Total Reports</Card.Title>
                    <Card.Text className="m-0">{totalReports || "0"}</Card.Text>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={3} className="mb-2 pt-2">
            <Card className="app-card bg-light border h-100">
              <Card.Body>
                <div className="d-flex gap-3">
                  <div className="study_icopn">
                    <FaChartLine className="bg-white text-info shadow-sm" />
                  </div>
                  <div>
                    <Card.Title style={{ fontSize: 16 }}>Total Runs</Card.Title>
                    <Card.Text className="m-0">{totalRuns || "0"}</Card.Text>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={3} className="mb-2 pt-2">
            <Card className="app-card bg-light border h-100">
              <Card.Body>
                <div className="d-flex gap-3">
                  <div className="study_icopn">
                    <FaTable className="bg-white text-success shadow-sm" />
                  </div>
                  <div>
                    <Card.Title style={{ fontSize: 16 }}>Categories</Card.Title>
                    <Card.Text className="m-0">{categories.length - 1}</Card.Text>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={3} className="mb-2 pt-2">
            <Card className="app-card bg-light border h-100">
              <Card.Body>
                <div className="d-flex gap-3">
                  <div className="study_icopn">
                    <FaCalendarAlt className="bg-white text-muted shadow-sm" />
                  </div>
                  <div>
                    <Card.Title style={{ fontSize: 16 }}>Last Generated</Card.Title>
                    <Card.Text className="m-0">{reports.sort((a, b) => new Date(b.lastRun) - new Date(a.lastRun))[0]?.lastRun || "—"}</Card.Text>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Reports Grid */}
      <SplitPane
        split="vertical"
        minSize={20}
        style={{ height: "calc(100vh - 292px)"}}
        className="position-relative"
        defaultSize={"100%"}
      >
        {loading ? (
          <div className="d-flex justify-content-center align-items-center h-100">
            <div className="text-center">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="text-muted">Generating report...</div>
            </div>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="d-flex justify-content-center align-items-center h-100">
            <div className="text-center text-muted">
              <FaFileAlt size={48} className="mb-3 opacity-50" />
              <div className="fs-5">No reports found</div>
              <div className="small">Try adjusting your search or filter</div>
            </div>
          </div>
        ) : (
          <Container fluid className="py-3">
            <Row className="g-2">
              {filteredReports.map((report, index) => (
                <Col key={report.id || index} xs={12} md={6} xl={4}>
                  <Card className="app-card bg-light border h-100">
                    <Card.Body>
                      <div className="d-flex gap-3">
                        <div className="study_icopn">
                          {report.icon}
                        </div>
                        <div>
                          <h6 className="m-0 fw-bold">{report.name}</h6>
                          <Form.Label className="text-muted small">{report.description}</Form.Label>
                        </div>
                      </div>

                      <div className="mb-2 hstack gap-1 flex-wrap">
                        <Badge 
                          className={`bg-${getCategoryColor(report.category)} br3 px-2 py-1 text-capitalize`}
                          style={{padding: "1px 10px"}}
                        >
                          {report.category}
                        </Badge>
                        {report.tags.map((tag, i) => (
                          <Badge 
                            key={i} 
                            className="bg-secondary bg-opacity-10 text-secondary br3 px-2 py-1 text-capitalize"
                            style={{padding: "1px 10px"}}
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>

                      <hr className="my-2" />

                      <div className="d-flex justify-content-between small text-muted">
                        <div>
                          <span>Last run</span>
                          <div className="fw-semibold text-dark">{report.lastRun}</div>
                        </div>
                        <div className="text-end">
                          <span>Runs</span>
                          <div className="fw-semibold text-dark">{report.runs}</div>
                        </div>
                      </div>
                    </Card.Body>
                    <Card.Footer className="bg-light border-top d-flex justify-content-end gap-2">
                      <button
                        style={{padding: "1px 10px"}}
                        className="updateProfile"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRunReport(report);
                        }}
                      >
                        Run
                      </button>
                      <button
                        className="updateProfile"
                        style={{padding: "1px 10px"}}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportReport(report);
                        }}
                      >
                        Export
                      </button>
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        )}
      </SplitPane>

      {/* Modals */}
      <ReportViewer
        Show={!toggles.toggle1}
        Hide={toggles.setToggle1}
        Title={selectedReport?.name || "Report Viewer"}
        reportData={reportData}
        onExport={() => {
          if (selectedReport) {
            toggles.setToggle1();
            handleExportReport(selectedReport);
          }
        }}
        onPrint={() => {
          console.log("Print report");
        }}
      />

      <ReportExport
        Show={!toggles.toggle2}
        Hide={toggles.setToggle2}
        Title="Export Report"
        reportData={selectedReport}
        onConfirm={(format) => {
          console.log(`Exporting ${selectedReport?.name} as ${format}`);
          toggles.setToggle2();
        }}
      />
    </>
  );
};

export default Reports;